import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { FinancialInstitution } from '@ld.consumer-portal/models';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface BankSelectionEvent {
  institution: FinancialInstitution;
}

@Component({
  selector: 'ld-bank-selection-modal',
  imports: [BaseModalComponent, FormsModule, MatIconModule, MatProgressSpinnerModule, TranslocoDirective],
  templateUrl: './bank-selection-modal.component.html',
  styleUrl: './bank-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BankSelectionModalComponent {
  readonly popularInstitutions = input.required<FinancialInstitution[]>();

  readonly searchResults = input<FinancialInstitution[]>([]);

  readonly isLoading = input<boolean>(false);

  readonly isSearching = input<boolean>(false);

  readonly select = output<BankSelectionEvent>();

  readonly search = output<string>();

  readonly cancel = output<void>();

  protected readonly searchQuery = signal('');

  protected readonly selectedInstitution = signal<FinancialInstitution | null>(null);

  protected readonly showingSearchResults = computed(() => this.searchQuery().length >= 2);

  protected readonly displayedInstitutions = computed(() => {
    if (this.showingSearchResults()) {
      return this.searchResults();
    }
    return this.popularInstitutions();
  });

  protected onSearchInput(query: string): void {
    this.searchQuery.set(query);
    if (query.length >= 2) {
      this.search.emit(query);
    }
  }

  protected clearSearch(): void {
    this.searchQuery.set('');
    this.selectedInstitution.set(null);
  }

  protected selectInstitution(institution: FinancialInstitution): void {
    this.selectedInstitution.set(institution);
  }

  protected onConfirm(): void {
    const institution = this.selectedInstitution();
    if (institution && !this.isLoading()) {
      this.select.emit({ institution });
    }
  }

  protected onCancel(): void {
    if (!this.isLoading()) {
      this.cancel.emit();
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isLoading()) {
      this.cancel.emit();
    }
  }

  private readonly localBankLogos: Record<string, string> = {
    chase: 'assets/banks/Chase_logo.png',
    'bank of america': 'assets/banks/Bank_of_America_logo.png',
    'wells fargo': 'assets/banks/Wells_Fargo_Bank_logo.png',
    'capital one': 'assets/banks/Capital_One_logo.png',
    citibank: 'assets/banks/Citibank_logo.png',
    'pnc bank': 'assets/banks/PNC_logo.png',
    pnc: 'assets/banks/PNC_logo.png',
    'td bank': 'assets/banks/TD_Bank_logo.png',
    'us bank': 'assets/banks/US_Bank_logo.png',
    'u.s. bank': 'assets/banks/US_Bank_logo.png',
  };

  protected getInstitutionLogo(institution: FinancialInstitution): string | null {
    return institution.logoUrl || institution.logo || this.localBankLogos[institution.name.toLowerCase()] || null;
  }

  protected getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
}
